import type { SceneImageRef } from './spread-scene-adapter';
import { imageDecodeVersion } from './decode-image';

export interface VersionedImage {
  preservePngRgba?: boolean;
  version?: string;
  /** Photo identity of the decoded bytes (SceneImageRef.imageId) — pairs a cache entry with its photo across renditions. */
  imageId?: string;
}

export interface TierEntry {
  preservePngRgba?: boolean;
  ref: string;
  url: string;
  imageId?: string;
  /** Ladder rung of `url` — a successful decode raises the photo's session rung floor by it. */
  rungPx?: number;
}

export interface ImageTierPlan {
  cacheHits: Record<string, VersionedImage>;
  // [0] thumbHash placeholders · [1] thumbs + single-tier refs (masks, smart objects) · [2] final targets
  tiers: [TierEntry[], TierEntry[], TierEntry[]];
  /** Same PHOTO, new rendition: the on-screen bitmap stays (cacheHits) while the target url decodes later,
   *  in the caller's background lane — swapping through blur/thumb tiers would flash mid-interaction. */
  deferred: TierEntry[];
}

export function planImageTiers<T extends VersionedImage>(
  refs: readonly SceneImageRef[],
  prevImages: Record<string, T> | undefined,
  // Refs whose frame must keep the pixels it has rather than fall back to the blur — scoped to a just-committed
  // photo-swap's settle render (see rust-overlay-controller's pendingSwapSettleRefs), not a blanket rule.
  holdPixelRefs?: ReadonlySet<string>,
  // Refs of frames that were EMPTY a moment ago and just received a photo (double-click, drag into a hole) —
  // see rust-overlay-controller's freshPlacementRefs.
  freshPlacementRefs?: ReadonlySet<string>,
): ImageTierPlan {
  const cacheHits: Record<string, VersionedImage> = {};
  const placeholders: TierEntry[] = [];
  const thumbs: TierEntry[] = [];
  const finals: TierEntry[] = [];
  const deferred: TierEntry[] = [];

  for (const r of refs) {
    const cached = prevImages?.[r.ref];
    if (cached && cached.version === imageDecodeVersion(r.url, r.preservePngRgba)) {
      cacheHits[r.ref] = cached;
      continue;
    }

    // Same photo at a stale rendition (frame resized under a slider, template regrew it): what is on screen is
    // RIGHT, only its resolution lags. Keep the current bitmap and lift the resolution in the deferred lane —
    // routing it through the blur/thumb waves would visibly replace a finished photo mid-interaction.
    if (cached && !!cached.preservePngRgba === !!r.preservePngRgba && r.imageId && cached.imageId === r.imageId) {
      cacheHits[r.ref] = cached;
      deferred.push({
        ref: r.ref,
        url: r.url,
        imageId: r.imageId,
        rungPx: r.rungPx,
        ...(r.preservePngRgba && { preservePngRgba: true }),
      });
      continue;
    }

    // A frame settling a photo swap it just committed never falls back to its thumbHash blur: the hash decodes at
    // its own coarse aspect, so the swap's landing frame would show a blurred, differently-cropped photo and jerk
    // back on the next tier — the drag already reads as one step, its settle has to look like one too. Any OTHER
    // replacement of a frame's pixels (a panel drop onto an occupied slot, a re-crop) still wants the blur right
    // away: withholding it just because the frame wasn't empty reads as a stuck frame, not a swap. An empty frame
    // always gets the blur, swap or not — it has no pixels to hold onto in the first place.
    const identity = { ...(r.imageId ? { imageId: r.imageId } : {}), ...(r.preservePngRgba && { preservePngRgba: true }) };
    // A photo just placed into an EMPTY frame arrives in ONE frame: it has no pixels to hold, so every rung of
    // the ladder would repaint it (blur → thumb → photo) and one user action would read as three steps.
    if (freshPlacementRefs?.has(r.ref)) {
      finals.push({ ref: r.ref, url: r.url, ...identity, rungPx: r.rungPx });
      continue;
    }

    const holdsPixels = !!cached && !!holdPixelRefs?.has(r.ref);
    if (!holdsPixels && r.placeholderUrl && r.placeholderUrl !== r.url) {
      placeholders.push({ ref: r.ref, url: r.placeholderUrl, ...identity });
    }

    if (r.thumbUrl && r.thumbUrl !== r.url) {
      thumbs.push({ ref: r.ref, url: r.thumbUrl, ...identity });
    }

    if (!r.thumbUrl && !r.placeholderUrl) {
      // Single-tier refs decode in the thumb wave so masked/smart boxes can surface before the final wave —
      // unless the ref blocks, and then the first paint waits rather than compositing without it.
      (r.blocking ? placeholders : thumbs).push({ ref: r.ref, url: r.url, ...identity, rungPx: r.rungPx });
    } else {
      finals.push({ ref: r.ref, url: r.url, ...identity, rungPx: r.rungPx });
    }
  }

  return { cacheHits, tiers: [placeholders, thumbs, finals], deferred };
}

// A box whose drawable needs a companion image (its mask) must appear atomically with it —
// the engine paints a maskless texture full-bleed opaque if the mask bytes are absent.
export function visibleImages<T>(decoded: Record<string, T>, refs: readonly SceneImageRef[]): Record<string, T> {
  const visible: Record<string, T> = {};
  for (const r of refs) {
    const img = decoded[r.ref];
    if (!img) continue;
    if (r.requiresRef && !decoded[r.requiresRef]) continue;
    visible[r.ref] = img;
  }

  return visible;
}
